import React, { useState, useEffect } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Check } from 'lucide-react';
import { employeeNamesService } from '@/services/employeeNames';

/**
 * Settings page for AI DevQuest app
 */
const AIDevQuestSettings: React.FC = () => {
  usePageTitle('AI DevQuest - Settings');
  
  const { userEmail } = useAuth();
  const [displayName, setDisplayName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Settings state
  const [autoPlayVideos, setAutoPlayVideos] = useState(false);
  const [showCompletedLessons, setShowCompletedLessons] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(false);
  
  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      if (!userEmail) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Get user display name
        const userName = await employeeNamesService.getNameFromEmail(userEmail);
        setDisplayName(userName || formatName(userEmail));
        
        // Load user settings from localStorage
        const savedSettings = localStorage.getItem(`ai-devquest-settings-${userEmail}`);
        if (savedSettings) {
          const settings = JSON.parse(savedSettings);
          setAutoPlayVideos(settings.autoPlayVideos ?? false);
          setShowCompletedLessons(settings.showCompletedLessons ?? true);
          setEmailNotifications(settings.emailNotifications ?? false);
        }
      } catch (err) {
        console.error('Error loading user data:', err);
        setError('Failed to load user data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserData();
  }, [userEmail]);
  
  // Format name from email
  const formatName = (email: string): string => {
    return email
      .split('@')[0]
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };
  
  // Save settings
  const handleSaveSettings = () => {
    if (!userEmail) return;
    
    try {
      const settings = {
        autoPlayVideos,
        showCompletedLessons,
        emailNotifications
      };
      
      localStorage.setItem(`ai-devquest-settings-${userEmail}`, JSON.stringify(settings));
      
      setSuccess('Settings saved successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again.');
    }
  };
  
  // Reset settings
  const handleResetSettings = () => {
    setAutoPlayVideos(false);
    setShowCompletedLessons(true);
    setEmailNotifications(false);
    
    setSuccess('Settings reset to defaults');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccess(null);
    }, 3000);
  };
  
  if (error) {
    return (
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="text-center mt-4">
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">Manage your AI DevQuest preferences</p>
      </div>
      
      {/* Success Message */}
      {success && (
        <Alert className="mb-6 bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-800">
          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-600 dark:text-green-400">{success}</AlertDescription>
        </Alert>
      )}
      
      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>User Preferences</CardTitle>
          <CardDescription>Customize your learning experience</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Video Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Video Settings</h3>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="autoplay">Auto-play videos</Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Automatically play videos when opening a lesson
                </p>
              </div>
              <Switch
                id="autoplay"
                checked={autoPlayVideos}
                onCheckedChange={setAutoPlayVideos}
              />
            </div>
          </div>
          
          {/* Display Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Display Settings</h3>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="completed-lessons">Show completed lessons</Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Display lessons you've already completed
                </p>
              </div>
              <Switch
                id="completed-lessons"
                checked={showCompletedLessons}
                onCheckedChange={setShowCompletedLessons}
              />
            </div>
          </div>
          
          {/* Notification Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Notification Settings</h3>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="email-notifications">Email notifications</Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Receive email notifications about new lessons
                </p>
              </div>
              <Switch
                id="email-notifications"
                checked={emailNotifications}
                onCheckedChange={setEmailNotifications}
              />
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={handleResetSettings}>
              Reset to Defaults
            </Button>
            <Button onClick={handleSaveSettings}>
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIDevQuestSettings;